import { useState } from "react";
import HeaderProperty from "./Show/HeaderProperty";
import { sanitizeValues } from "../../models/utils";
import useProperties from "../../hooks/use-properties";
import ContentLayout from "../UI/Layout/ContentLayout.tsx";
import PermissionScope from "../../store/PermissionScope";
import VueEnsemble from "./Show/VueEnsemble";
import { Tabs } from "../UI/Tabs/Tabs";
import useTenants from "../../hooks/use-tenants";
import { TenantStatus } from "../../models/tenant_old.js";
import EdlShow from "../Edl/EdlShow";
import ShowCandidatList from "../Tenant/ShowCandidatList";
import DocumentShowList from "../File/DocumentShowList";
import { PropertyStatus } from "../../models/property";
import { useNavigate } from "react-router-dom";
import { BalanceDomain } from "../../utils/constants/balances";
import ComptaShow from "./ComptaShow";
import PropertyAccountingForm from "./PropertyAccountingForm";
import DropdownMenu from "../UI/DropdownMenu.tsx";
import { IconBook } from "@tabler/icons-react";
import BlurOverlay from "../UI/BlurOverlay";
import DisabledBienNote from "./DisabledBienNote";
import styles from "./PropertyShow.module.css";
import { isPropertyDisabled } from "../../utils/properties";
import CompteurShow from "./CompteurShow";
import MaintenanceLogShow from "./MaintenanceLogShow";
import useEdlPiecesPrompt from "../../hooks/use-edl-pieces-prompt.tsx";
import GuidedTour from "../GuidedTour/GuidedTour.tsx";
import { BIEN_GUIDE_ID, BIEN_GUIDE_STEPS } from "../GuidedTour/guides.ts";
import useOnboarding from "../../hooks/use-onboarding.ts";

export default function PropertyShow({ propertyId }) {
  const { properties, updateProperty, updatePropertyCles, deleteProperty } =
    useProperties();
  const property = properties.find((prop) => prop.id === parseInt(propertyId));

  const { tenants, isLoading } = useTenants();
  const [isAccountingFormOpen, setIsAccountingFormOpen] = useState(false);
  const { gating } = useOnboarding();
  const nav = useNavigate();
  const { confirmDialog, promptIfPiecesAdded } = useEdlPiecesPrompt(
    property?.id,
  );

  async function submitHandler(values) {
    const propertyBefore = property;
    await updateProperty(property.id, sanitizeValues(values));
    promptIfPiecesAdded(propertyBefore, values);
  }

  const candidats = tenants.filter(
    (tenant) =>
      tenant.id_bien === parseInt(propertyId) &&
      tenant.status === TenantStatus.Candidat.status,
  );

  const disabledProperty = isPropertyDisabled(property);
  if (!property) return null;

  return (
    <PermissionScope profileId={property?.id_profile_nom_owner}>
      {confirmDialog}
      <GuidedTour
        guideId={BIEN_GUIDE_ID}
        steps={BIEN_GUIDE_STEPS}
        run={gating && !disabledProperty}
        onFinish={(finished) => {
          if (finished) {
            nav("/bailleur");
          }
        }}
      />
      <ContentLayout
        header={
          property && (
            <HeaderProperty
              property={property}
              onDelete={async () => {
                try {
                  await deleteProperty(property.id);
                  nav("/bailleur/properties");
                } catch (error) {
                  console.error(error);
                }
              }}
              onArchive={async () => {
                try {
                  await updateProperty(property.id, {
                    status: PropertyStatus.Archive.status,
                  });
                } catch (error) {
                  console.error(error);
                }
              }}
              onReactivate={async () => {
                try {
                  await updateProperty(property.id, {
                    status: PropertyStatus.Actif.status,
                  });
                } catch (error) {
                  console.error(error);
                }
              }}
              onUpdate={submitHandler}
            />
          )
        }
        body={
          <Tabs id="prop-show" defaultValue="vue-ensemble">
            <div className="container bg-blue paddingT10 border-blue">
              <div className="content">
                <Tabs.List>
                  <Tabs.Tab value="vue-ensemble" dataTour="bien-tab-general">
                    Général
                  </Tabs.Tab>
                  {!disabledProperty && (
                    <>
                      <Tabs.Tab value="documents" dataTour="bien-tab-documents">
                        Documents
                      </Tabs.Tab>
                      <Tabs.Tab value="candidats" dataTour="bien-tab-candidats">
                        Candidats ({candidats.length})
                      </Tabs.Tab>
                      <Tabs.Tab value="edl" dataTour="bien-tab-edl">
                        Etat des lieux
                      </Tabs.Tab>
                      <Tabs.Tab value="compta" dataTour="bien-tab-compta">
                        Comptabilité
                      </Tabs.Tab>
                      <Tabs.Tab value="compteurs" dataTour="bien-tab-compteurs">
                        Compteurs
                      </Tabs.Tab>
                      <Tabs.Tab
                        value="maintenance"
                        dataTour="bien-tab-entretien"
                      >
                        Entretien
                      </Tabs.Tab>
                    </>
                  )}
                </Tabs.List>
              </div>
            </div>
            <div className="blueSpace"></div>
            <div className="content" style={{ position: "relative" }}>
              <div inert={disabledProperty ? "true" : undefined}>
                <Tabs.Panel value="vue-ensemble">
                  <VueEnsemble
                    property={property}
                    submitHandler={submitHandler}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="documents">
                  <DocumentShowList property={property} allowEdit allowDelete />
                </Tabs.Panel>
                <Tabs.Panel value="candidats">
                  <ShowCandidatList tenants={candidats} isLoading={isLoading} />
                </Tabs.Panel>
                <Tabs.Panel value="edl">
                  <EdlShow
                    propertyId={property?.id}
                    property={property}
                    updatePropertyCles={updatePropertyCles}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="compta">
                  <PropertyAccountingForm
                    open={isAccountingFormOpen}
                    onOpenChange={setIsAccountingFormOpen}
                    property={property}
                    updateProperty={updateProperty}
                  />
                  <ComptaShow
                    parentId={property?.id}
                    domaineBalance={BalanceDomain.PROPERTY}
                    extraMenuItems={
                      <DropdownMenu.Item
                        requiresWrite
                        icon={IconBook}
                        label="Comptes comptables du bien"
                        onClick={() => setIsAccountingFormOpen(true)}
                      />
                    }
                  />
                </Tabs.Panel>
                <Tabs.Panel value="compteurs">
                  <CompteurShow propId={property.id} />
                </Tabs.Panel>
                <Tabs.Panel value="maintenance">
                  <MaintenanceLogShow
                    domain={BalanceDomain.PROPERTY}
                    parentId={property.id}
                  />
                </Tabs.Panel>
              </div>
              {disabledProperty && (
                <BlurOverlay className={styles.overlay}>
                  <DisabledBienNote className={styles.disabledNote} />
                </BlurOverlay>
              )}
            </div>
          </Tabs>
        }
      />
    </PermissionScope>
  );
}
